import * as apiWrapper from './Wrapper';
import { base64ToUint8Array, uint8ArrayToString } from '../Converter';
import { wsApi } from './WebSocketWrapper';

import { ErrorBreadcrumb } from '@egr/xbox/utils/Error';
import { assert, withDebugTimer } from '@egr/xbox/utils/Debug';
import { getFilenameFromPath, pathIsDirectory, stripTrailingSlashFromPath } from '@egr/xbox/utils/Path';
import { parseFilename } from '@egr/xbox/utils/String';

import { Lock, LockContext } from '@egr/wcf/modules/utils/async';
import { isNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { fromBlob } from '@egr/wcf/modules/utils/arraybuffer';
import { safelyParseJSON } from '@egr/xbox/utils/Json';

export const ChunkSize: number = 2097152; // 2mb

export type Brakesman = () => Promise<void>;

const LockMap: Map<string, Lock> = new Map<string, Lock>();
async function lockFile(path: string): Promise<VoidFunction> {
    let lock: Lock | undefined = LockMap.get(path);
    if (lock == null) {
        lock = new Lock();
        LockMap.set(path, lock);
    }

    // Note: the function only ensures that the write operations do not overwrite each
    // other. However, it is probably wrong to write to the same file more than once at
    // the same time.
    assert(lock.isLocked === false, 'File should not be locked', path);

    const context: LockContext = await lock.acquireLock();
    return (): void => {
        context.unlock();

        if (lock != null && !lock.isLocked) {
            LockMap.delete(path);
        }
    };
}

export async function getSubDirectories(path: string, trailingSlash: boolean = true): Promise<Array<string>> {
    const entries: Array<string> = await apiWrapper.list(path);

    const directories: Array<string> = entries.filter(pathIsDirectory);

    if (trailingSlash === false) {
        return directories.map(stripTrailingSlashFromPath);
    }

    return directories;
}

function breadcrumbHook(
    breadcrumb: ErrorBreadcrumb,
    additionalTags?: Record<string, string>,
    error?: Error
): ErrorBreadcrumb {
    breadcrumb.category = 'app-api';
    if (breadcrumb.data?.extension != null) {
        breadcrumb.data.extension = undefined;
    }

    return breadcrumb;
}

export async function readTextFile(
    path: string,
    brakesman?: Brakesman
): Promise<string> {
    const unlock: VoidFunction = await lockFile(path);
    try {
        const filename: string = getFilenameFromPath(path);
        const [, extension] = parseFilename(filename);
        return await withDebugTimer(
            'readTextFile',
            (): Promise<string> => {
                return _readTextFile(path, brakesman);
            },
            {
                filename,
                extension
            },
            breadcrumbHook
        );
    } finally {
        unlock();
    }
}

async function _readTextFile(
    path: string,
    brakesman?: Brakesman
): Promise<string> {
    if (wsApi != null) {
        try {
            return (await wsApi.readFiles([{
                binary: false,
                path
            }]))[0] as string;
        } catch (_) {
            return null!;
        }
    }

    let fd: string;
    try {
        fd = await apiWrapper.open(path, 'r', '');
    } catch (_) {
        // no such file
        return null!; // FixMe -> change function signature or calling code
    }

    let data: string = '';
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (brakesman != null) {
            await brakesman();
        }

        const chunk: string = await apiWrapper.read(fd, ChunkSize);
        data += chunk;

        if (chunk.length < ChunkSize) {
            void apiWrapper.close(fd); // close async
            return data;
        }
    }
}

export async function readJsonFile<T = unknown>(path: string, brakesman?: Brakesman): Promise<T | null> {
    const content: string | null = await readTextFile(path, brakesman);
    if (isNullOrEmpty(content)) {
        return null;
    }

    return safelyParseJSON<T>(content) ?? null;
}

export async function readBinaryFile(
    path: string,
    brakesman?: Brakesman
): Promise<ArrayBuffer> {
    const unlock: VoidFunction = await lockFile(path);
    try {
        const filename: string = getFilenameFromPath(path);
        const [, extension] = parseFilename(filename);
        return await withDebugTimer(
            'readBinaryFile',
            (): Promise<ArrayBuffer> => {
                return _readBinaryFile(path, brakesman);
            },
            {
                filename,
                extension
            },
            breadcrumbHook
        );
    } finally {
        unlock();
    }
}

async function _readBinaryFile(
    path: string,
    brakesman?: Brakesman
): Promise<ArrayBuffer> {
    if (wsApi != null) {
        try {
            return (await wsApi.readFiles([{
                binary: true,
                path
            }]))[0] as ArrayBuffer;
        } catch (_) {
            return null!;
        }
    }

    let fd: string;
    try {
        fd = await apiWrapper.open(path, 'r', 'base64');
    } catch (_) {
        // no such file
        return null!; // FixMe -> change function signature or calling code
    }

    let data: Uint8Array = new Uint8Array(0);
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (brakesman != null) {
            await brakesman();
        }

        const chunk: Uint8Array = base64ToUint8Array(
            await apiWrapper.read(fd, ChunkSize)
        );

        if (data.byteLength === 0 && chunk.byteLength < ChunkSize) {
            void apiWrapper.close(fd); // close async
            return chunk.buffer;
        }

        const newData: Uint8Array = new Uint8Array(data.byteLength + chunk.byteLength);
        newData.set(data, 0);
        newData.set(chunk, data.byteLength);

        data = newData;

        if (chunk.byteLength !== ChunkSize) {
            void apiWrapper.close(fd); // close async
            return data.buffer;
        }
    }
}

export function writeFile(
    path: string,
    data: ArrayBuffer | Blob | string,
    brakesman?: Brakesman | null
): Promise<void> {
    const filename: string = getFilenameFromPath(path);
    const [, extension] = parseFilename(filename);
    return withDebugTimer(
        'writeFile',
        (): Promise<void> => {
            return __writeFile(path, data, brakesman);
        },
        {
            filename,
            extension
        },
        breadcrumbHook
    );
}

async function __writeFile(
    path: string,
    data: ArrayBuffer | Blob | string,
    brakesman?: Brakesman | null
): Promise<void> {
    const unlock: VoidFunction = await lockFile(path);
    try {
        if (wsApi != null) {
            return wsApi.writeFiles([{
                path,
                data
            }]);
        }
        return await _writeFile(path, data, brakesman);
    } finally {
        unlock();
    }
}

async function _writeFile(path: string, data: ArrayBuffer | Blob | string, brakesman: Brakesman | null = null): Promise<void> {
    if (data instanceof Blob) {
        return _writeFile(path, await fromBlob(data), brakesman);
    }

    let fd: string | null = null;
    try {
        if (data instanceof ArrayBuffer) {
            fd = await apiWrapper.open(path, 'w', 'base64');
            for (let i: number = 0, l: number = data.byteLength; i < l; i +=  ChunkSize) {
                if (brakesman != null) {
                    await brakesman();
                }

                await apiWrapper.write(
                    fd,
                    btoa(
                        uint8ArrayToString(
                            new Uint8Array(data.slice(i, i + ChunkSize))
                        )
                    )
                );
            }
        } else {
            fd = await apiWrapper.open(path, 'w', '');
            for (let i: number = 0, l: number = data.length; i < l; i +=  ChunkSize) {
                if (brakesman != null) {
                    await brakesman();
                }

                await apiWrapper.write(
                    fd,
                    data.slice(i, i + ChunkSize)
                );
            }
        }

        return apiWrapper.close(fd);
    } catch (error) {
        if (fd != null) {
            try {
                await apiWrapper.close(fd);
            } catch (_) {
                // ignore error
            }
        }

        return Promise.reject(error);
    }
}

export async function *findAllFiles(path: string, maxdepth: number = 1000, errorLogger?: (path: string, error: Error | string) => void): AsyncIterableIterator<string> {
    if (maxdepth < 1) {
        return;
    }

    try {
        const entries: Array<string> = await apiWrapper.list(path);
        for (const entry of entries) {
            if (pathIsDirectory(entry)) {
                yield* findAllFiles(path + entry, maxdepth - 1, errorLogger);
            } else {
                yield path + entry;
            }
        }
    } catch (error) {
        errorLogger?.(path, error);
    }
}